// @givmo/observability — Sentry init helper.
//
// The single entry point every web app calls once at boot. It wraps `Sentry.init` with the
// non-negotiable privacy posture from 03 §2:
//   - `beforeSend` + `beforeBreadcrumb` scrubbing are ALWAYS installed (a caller cannot
//     opt out — the hooks are forced on regardless of any options passed).
//   - `sendDefaultPii: false` — never auto-attach IP / cookies / request bodies.
//   - DSN is env-injected by the app (never hardcoded here). When no DSN is provided
//     (local dev, tests, an env that hasn't provisioned Sentry) init is a safe no-op and
//     returns `undefined`, so importing this package never forces a Sentry connection.
//
// The `client-headers` helper is the request-stamping side; this is the crash/error side.
import * as Sentry from '@sentry/react';
import type { BrowserOptions } from '@sentry/react';
import { beforeSend, beforeBreadcrumb } from './scrub';
import { redactObject } from './redaction';
import { clientHeaders, type ClientIdentity } from './client-headers';

/** The Sentry client returned by `Sentry.init` (or `undefined` when init is skipped). */
export type ObservabilityClient = ReturnType<typeof Sentry.init>;

export interface ObservabilityConfig {
  /**
   * Sentry DSN, env-injected by the host app (e.g. from `import.meta.env.VITE_SENTRY_DSN`).
   * When empty/undefined, Sentry is NOT initialized and `initObservability` returns `undefined`.
   */
  dsn?: string;
  /** Deploy environment tag (`local` | `dev` | `staging` | `prod`). */
  environment?: string;
  /** Release identifier — typically the app version (parity with `clientIdentity.version`). */
  release?: string;
  /** Client identity used for the `release`/`dist` defaults + reusable by the api-client. */
  clientIdentity?: ClientIdentity;
  /**
   * Escape hatch for additional `Sentry.init` options (integrations, sample rates, etc.).
   * The scrubbing hooks and `sendDefaultPii: false` are applied AFTER this and cannot be
   * overridden — privacy is not a caller concern.
   */
  options?: Omit<BrowserOptions, 'dsn' | 'beforeSend' | 'beforeBreadcrumb' | 'sendDefaultPii'>;
}

/**
 * Initialize Sentry with mandatory PII scrubbing. Idempotent-friendly: a no-DSN call is a
 * no-op. Returns the Sentry `Client` when initialized, or `undefined` when skipped (no DSN).
 *
 * @returns the initialized client, or `undefined` if init was skipped (no DSN).
 */
export function initObservability(config: ObservabilityConfig = {}): ObservabilityClient {
  const dsn = config.dsn?.trim();
  if (dsn === undefined || dsn.length === 0) {
    return undefined;
  }

  const release = config.release ?? config.clientIdentity?.version;

  return Sentry.init({
    ...config.options,
    dsn,
    environment: config.environment,
    release,
    dist: config.clientIdentity?.build,
    // Privacy posture — forced on, not caller-overridable.
    sendDefaultPii: false,
    beforeSend,
    beforeBreadcrumb,
  });
}

/**
 * Report a NON-FATAL condition a surface tolerated — a wire value this build does not know, a
 * fallback it took — as a warning-level message with redacted context. A no-op when Sentry was
 * never initialized (no DSN), exactly like the crash path, so a caller never guards it.
 */
export function captureNonFatal(message: string, context?: Record<string, unknown>): void {
  Sentry.captureMessage(message, {
    level: 'warning',
    ...(context === undefined ? {} : { extra: redactObject(context) }),
  });
}

/**
 * Build the `X-Client-*` headers for an app's identity. Re-exported convenience so a host
 * app can derive headers from the same `clientIdentity` it passed to `initObservability`.
 */
export function clientHeadersFor(config: ObservabilityConfig): Record<string, string> {
  return config.clientIdentity ? clientHeaders(config.clientIdentity) : {};
}
